import { css } from '@emotion/core';
import { Button } from '@material-ui/core';
import React from 'react';

import { JobStatus } from 'shared/models/job';
import { ScoringBriefResultPreview } from 'shared/models/profile-score';
import { hasRole } from 'shared/models/user';

import { previewScoringBrief } from '../api';
import { RichTextField } from '../core-ui/form-fields';
import { JobFormData } from '../graphql/queries/job-form';
import { useModal } from '../hooks/use-modal';
import { useSession } from '../hooks/use-session';
import { useFormContext } from './form-context';
import { JobScoringBriefPreviewDialog } from './job-scoring-brief-preview-dialog';

const styles = css`
    .read-only-field {
        .rich-text-field {
            opacity: 1;
            --rte-disabled-color: rgba(0, 0, 0, 0.87);
        }

        .rich-text-field::after,
        .rich-text-field:hover::after {
            transform: scaleX(0);
        }
    }
`;

export const JobScoringBriefField: React.FC = () => {
    const { data, onFieldChange, disabled } = useFormContext<JobFormData>();
    const { user, userPermissions } = useSession();
    const { setAlert, showLoading, hideLoading } = useModal();
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewRows, setPreviewRows] = React.useState<ScoringBriefResultPreview[]>([]);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [directionsDisplayed, setDirectionsDisplayed] = React.useState(false);

    const canEditScoringBrief =
        data.status !== JobStatus.Archived &&
        (data.accountManagerId === user.id || hasRole(userPermissions, 'job_details_editor'));
    const currentBrief = data.scoringAIContext?.scoringBrief ?? '';
    const isFieldDisabled = disabled || !canEditScoringBrief;
    const fieldClassName = isFieldDisabled ? 'read-only-field' : undefined;

    const handleScoringBriefChange = (value: string) => {
        onFieldChange('scoringAIContext')({
            ...(data.scoringAIContext ?? {}),
            briefSource: 'recruiter',
            briefUpdatedAt: Date.now(),
            scoringBrief: value
        });
    };

    const handlePreviewClick = async () => {
        try {
            setPreviewLoading(true);
            showLoading();
            const response = await previewScoringBrief(data.id, currentBrief);
            if (!response.success) {
                throw new Error('Failed to preview scoring brief impact');
            }
            setPreviewRows(response.data?.rows ?? []);
            setPreviewOpen(true);
        } catch {
            setAlert('Error', 'Failed to preview scoring brief impact');
        } finally {
            hideLoading();
            setPreviewLoading(false);
        }
    };

    const handleFieldFocus = () => {
        if (!directionsDisplayed) {
            const guidelines = (
                <div>
                    <p>
                        Brief quality AND structure has the MOST impact on candidate evaluation. Make sure to follow a
                        consistent structure and have these sections:
                    </p>
                    <ol>
                        <li>What this job is</li>
                        <li>Good fit</li>
                        <li>Bad fit</li>
                    </ol>
                    <p>
                        Do NOT include:
                        <ul>
                            <li>
                                HARD FILTERS like (these should be enforced at search creation time, not in the AI
                                profile fit evaluation):
                                <ul>
                                    <li>"must have 5 years experience in X" or "must have used Y tool"</li>
                                    <li>"must have worked at a Series A company"</li>
                                </ul>
                            </li>
                            <li>
                                Soft skills that are hard to evaluate from a profile like "good communicator" (these are
                                better assessed during a phone screen)
                            </li>
                        </ul>
                    </p>
                    <p>
                        After editing, make sure to "Preview" to check how this brief would have evaluated recent
                        candidates. If the results don't make sense, edit the brief and preview again until it looks
                        good.
                    </p>
                </div>
            );
            setAlert('Scoring brief guidelines', guidelines);
            setDirectionsDisplayed(true);
        }
    };

    const handlePreviewClose = () => {
        setPreviewOpen(false);
    };

    const previewButton = canEditScoringBrief ? (
        <div className="preview-outreach">
            <Button onClick={handlePreviewClick} disabled={disabled || previewLoading}>
                Preview
            </Button>
        </div>
    ) : null;

    return (
        <div css={styles} className="job-field-row preview-container">
            <RichTextField
                className={fieldClassName}
                label="Scoring Brief"
                value={currentBrief}
                onChange={handleScoringBriefChange}
                disabled={isFieldDisabled}
                format="markdown"
                toolbar={isFieldDisabled ? 'none' : undefined}
                onFocus={handleFieldFocus}
            />
            {previewButton}
            <JobScoringBriefPreviewDialog open={previewOpen} rows={previewRows} onClose={handlePreviewClose} />
        </div>
    );
};
